import { useState, useRef, useEffect } from 'react';

/**
 * @typedef {object} DropdownOption
 * @property {string} value - Unique option value
 * @property {string} label - Display label for the option
 */

/**
 * A single-select dropdown with an optional search input.
 * Uses the shared `.dropdown` CSS classes from dropdown.css.
 *
 * @param {object} props
 * @param {DropdownOption[]} props.options - List of selectable options
 * @param {string} props.value - Currently selected value
 * @param {(value: string) => void} props.onChange - Called on selection
 * @param {string} [props.placeholder] - Placeholder when nothing is selected
 * @param {boolean} [props.allowSearch] - Show a search input inside the list
 */
const SearchableDropdown = ({
    options,
    value,
    onChange,
    placeholder = 'Selecteer...',
    allowSearch = false,
}) => {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    /** @type {React.RefObject<HTMLDivElement>} */
    const containerRef = useRef(null);

    /* Close when clicking outside the component */
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current &&
                !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const selectedLabel = options.find(o => o.value === value)?.label
        ?? placeholder;

    const visibleOptions = searchTerm
        ? options.filter(o =>
            o.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : options;

    /**
     * @param {DropdownOption} option
     */
    const handleSelect = (option) => {
        onChange(option.value);
        setOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="searchable-dropdown dropdown" ref={containerRef}>
            <div
                className="dropdown-header"
                onClick={() => setOpen(prev => !prev)}
            >
                <span>{selectedLabel}</span>
                <span className={`arrow ${open ? 'open' : ''}`} />
            </div>
            {open && (
                <div className="dropdown-list">
                    {allowSearch && (
                        /* Search input inside the open list */
                        <div style={{ padding: '6px 10px' }}>
                            <input
                                autoFocus
                                type="text"
                                placeholder="Zoeken..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                /* Prevent clicks on the input from closing */
                                onClick={(e) => e.stopPropagation()}
                                style={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </div>
                    )}
                    {/* Een leeg vak laat raden: niets gevonden, nog bezig of stuk? */}
                    {searchTerm && visibleOptions.length === 0 && (
                        <div className="dropdown-empty">Niets gevonden voor '{searchTerm}'.</div>
                    )}
                    {visibleOptions.map(option => (
                        <div
                            key={option.value}
                            className="dropdown-item"
                            onClick={() => handleSelect(option)}
                        >
                            {option.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SearchableDropdown;
